import Vue from 'vue';
import Vuex, { MutationTree, ActionTree } from 'vuex';
import type { User } from '@/models/user';

import axios from 'axios';
import type { Company } from '@/models/company';
import type { UserSetting } from '@/models/userSetting';
import { userWebservice } from '@/webservices/user';
import { companyWebservice } from '@/webservices/company';
import type { CompanySetting } from '@/models/companySetting';
import { setSentryUserId } from '@/main.sentry';

Vue.use(Vuex);

interface State {
    userInfo: User;
    company: Company;
    loggedIn: boolean;
}

const mutations: MutationTree<State> = {
    setUserInfo: (currentState, userInfo) => {
        currentState.userInfo = userInfo;
        setSentryUserId(userInfo.id);
        currentState.loggedIn = true;
    },

    setApiToken: (currentState, apiToken) => {
        // Update the local storage by hand
        localStorage.removeItem('api_token');
        localStorage.setItem('api_token', apiToken);
        axios.defaults.headers.common.Authorization = 'Bearer '.concat(apiToken);
        currentState.loggedIn = true;
    },

    addUserInfo(currentState, userInfo) {
        for (const element in userInfo) {
            // gets all the elements that are passed to addOrderInfo
            // and add them with their key/value to userInfo object
            if (userInfo.hasOwnProperty(element)) {
                currentState.userInfo[element] = userInfo[element];
            }
        }
    },

    logout(currentState) {
        currentState.loggedIn = false;
    },

    setCompany(currentState, company: Company) {
        currentState.company = company;
    },

    resetState() {
        // console.log('resetState FIRED FROM Account STORE');
        // acquire initial state
        const s = initialState();

        Object.keys(s).forEach((key) => {
            state[key] = s[key];
        });
    },
};

const actions: ActionTree<State, any> = {
    fetchCompanyByUser({ commit }): any {
        companyWebservice.getCompanyByUser().then((response) => {
            const company: Company = response && response.data;
            commit('setCompany', company);
        });
    },
    async getUserInfo({ commit }) {
        try {
            const res = await userWebservice.getUserInfo();
            commit('setUserInfo', res.data);
        } catch (error) {
            console.error(error);
            throw error;
        }
    },
};

const getters = {
    userInfo(currentState: State) {
        return currentState.userInfo;
    },

    loggedIn(currentState: State): boolean {
        const token = localStorage.getItem('api_token');
        if (token) {
            return true;
        }
        return false;
    },

    isEmailConfirmed(currentState: State): boolean {
        return currentState.userInfo.email_confirmed;
    },

    company(currentState: State): Company {
        return currentState.company;
    },

    companyProcessName: (currentState, currentGetters, rootState, rootGetters): string => {
        const currentLocale: string = rootGetters['utils/currentLocale'];

        return currentLocale === 'en'
            ? currentState.company.setting.x_en_process_title
            : currentState.company.setting.x_de_process_title;
    },

    companyProcessTabTitle: (currentState, currentGetters, rootState, rootGetters): string => {
        const currentLocale: string = rootGetters['utils/currentLocale'];

        return currentLocale === 'en'
            ? currentState.company.setting.x_en_tab_title
            : currentState.company.setting.x_de_tab_title;
    },

    companySetting(currentState: State): CompanySetting {
        return currentState.company.setting;
    },
};

function initialState() {
    return {
        loggedIn: false,
        userInfo: {
            id: 0,
            firstname: '',
            lastname: '',
            email: '',
            new_email: '',
            is_superadmin: false,
            email_confirmation_token: '',
            password: '',
            password_confirmation: '',
            new_password: '',
            email_confirmed: false,
            address: '',
            address_addition: '',
            postcode: '',
            city: '',
            country: '',
            role_id: 0,
            disabled: false,
            settings: {} as UserSetting,
            company_id: 0,
        },
        company: {
            id: 0,
            name: '',
            description: '',
            email: '',
            token: '',
            phone: '',
            address: '',
            address_addition: '',
            postcode: '',
            city: '',
            country: '',
            admin_id: 0,
            clonedData: false,
            logo_path: '',
            can_use_ms_graph: false,
            setting: {
                id: 0,
                feature_tags: false,
            } as CompanySetting,
        },
    };
}

const state: State = initialState();

const module = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};

export default module;
